/**
 * Employment Contract Configuration
 *
 * Comprehensive configuration for employment contract forms (Pracovní smlouva).
 * This configuration replaces the hardcoded AppleStyleEmploymentContractForm
 * with a declarative, maintainable structure.
 *
 * Supports multiple employment types:
 * - Full-time permanent positions (Na dobu neurčitou)
 * - Fixed-term contracts (Na dobu určitou)
 * - Part-time positions (Částečný úvazek)
 * - Agreement to perform work (DPČ - Dohoda o pracovní činnosti)
 * - Agreement to complete a job (DPP - Dohoda o provedení práce)
 *
 * @module configs/contracts/employmentContract
 * @author AI Lawyer System
 * @version 1.0.0
 */

import { ContractFormConfig, FieldType, FieldConfig } from '../../types/contractForm.types';

/**
 * Employment Contract Form Configuration
 *
 * Defines the complete structure for employment contract forms including:
 * - 7 steps: Employer Info, Employee Info, Position, Salary, Working Conditions, Additional Terms, Review
 * - 30+ fields with full validation
 * - Conditional logic for employment type-specific fields
 * - RAG integration for intelligent text field suggestions
 *
 * @constant
 */
export const employmentContractConfig: ContractFormConfig = {
  type: 'employment',
  subtype: undefined,

  metadata: {
    title: 'Pracovní smlouva',
    description: 'Konfigurace pro vytvoření pracovní smlouvy dle zákoníku práce',
    version: '1.0.0',
  },

  steps: [
    // ==================== STEP 0: Employer Information ====================
    {
      title: 'Kdo zaměstnává?',
      subtitle: 'Zadejte údaje o zaměstnavateli',
      description: 'Základní identifikační údaje zaměstnavatele',
      icon: 'Business',
      color: 'warning',
      fields: [
        {
          name: 'employer_name',
          label: 'Název firmy / Jméno zaměstnavatele',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Název zaměstnavatele je povinný' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. TechCorp s.r.o. nebo Jan Novák',
          helpText: 'Celý název firmy nebo jméno fyzické osoby - zaměstnavatele',
          examples: ['TechCorp s.r.o.', 'ABC Development a.s.', 'Jan Novák - OSVČ', 'IT Solutions spol. s r.o.'],
          ragEnabled: false,
        },
        {
          name: 'employer_address',
          label: 'Adresa sídla',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa zaměstnavatele je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          examples: [
            'Václavské náměstí 1, 110 00 Praha 1',
            'Náměstí Svobody 10, 602 00 Brno',
            'Hlavní 25, 301 00 Plzeň',
          ],
          ragEnabled: false,
        },
        {
          name: 'employer_id',
          label: 'IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'IČO zaměstnavatele je povinné' },
            {
              type: 'pattern',
              value: /^\d{8}$/,
              message: 'IČO musí mít 8 číslic',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '12345678',
          helpText: 'Identifikační číslo organizace',
          examples: ['12345678', '87654321'],
          ragEnabled: false,
        },
        {
          name: 'employer_contact',
          label: 'Kontakt (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'telefon nebo e-mail',
          helpText: 'Telefon nebo e-mail pro komunikaci',
          examples: ['+420 123 456 789', 'hr@firma.cz'],
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 1: Employee Information ====================
    {
      title: 'Kdo nastupuje?',
      subtitle: 'Zadejte údaje o zaměstnanci',
      description: 'Osobní údaje zaměstnance podle občanského průkazu',
      icon: 'Person',
      color: 'primary',
      fields: [
        {
          name: 'employee_name',
          label: 'Jméno a příjmení',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Jméno zaměstnance je povinné' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. Petra Nováková',
          examples: ['Petra Nováková', 'Tomáš Dvořák', 'Anna Svobodová', 'Pavel Novotný'],
          ragEnabled: false,
        },
        {
          name: 'employee_address',
          label: 'Adresa bydliště',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa zaměstnance je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          helpText: 'Trvalé bydliště zaměstnance',
          examples: [
            'Krásná 15, 120 00 Praha 2',
            'Dlouhá 8, 110 00 Praha 1',
            'Masarykova 20, 602 00 Brno',
          ],
          ragEnabled: false,
        },
        {
          name: 'employee_id',
          label: 'Rodné číslo',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo zaměstnance je povinné' },
            {
              type: 'pattern',
              value: /^\d{6}\/\d{4}$/,
              message: 'Neplatný formát rodného čísla (očekáváno: 123456/7890)',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890',
          examples: ['123456/7890', '654321/1234'],
          ragEnabled: false,
        },
        {
          name: 'employee_email',
          label: 'E-mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
          helpText: 'Firemní nebo osobní e-mail',
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 2: Position Details ====================
    {
      title: 'Jaká pozice?',
      subtitle: 'Popište pracovní místo a náplň práce',
      description: 'Druh práce a místo výkonu práce',
      icon: 'Work',
      color: 'secondary',
      fields: [
        {
          name: 'position_title',
          label: 'Název pozice',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Název pozice je povinný' },
          ],
          gridProps: { xs: 12, md: 8 },
          placeholder: 'např. Frontend Developer',
          helpText: 'Oficiální název pracovní pozice',
          examples: [
            'Frontend Developer',
            'Account Manager',
            'Produktový manažer',
            'UX/UI Designer',
            'Účetní',
            'HR specialista',
          ],
          ragEnabled: false,
        },
        {
          name: 'department',
          label: 'Oddělení (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 4 },
          placeholder: 'např. IT',
          helpText: 'Oddělení nebo tým',
          examples: ['IT', 'Marketing', 'HR', 'Finance', 'Obchod', 'Vývoj'],
          ragEnabled: false,
        },
        {
          name: 'job_description',
          label: 'Popis pracovní náplně',
          type: FieldType.MULTILINE,
          rows: 4,
          validation: [
            { type: 'required', message: 'Popis práce je povinný' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Detailně popište, co bude zaměstnanec dělat...',
          helpText: 'Čím detailnější popis, tím jasnější očekávání',
          examples: [
            'Vývoj webových aplikací v React a TypeScript, spolupráce s UX designéry, code review',
            'Péče o klíčové klienty, příprava obchodních nabídek, jednání s klienty',
            'Vedení účetnictví, příprava daňových přiznání, komunikace s úřady',
            'Návrh uživatelského rozhraní, tvorba prototypů, usability testování',
          ],
          ragEnabled: true,
        },
        {
          name: 'workplace_address',
          label: 'Místo výkonu práce (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12 },
          placeholder: 'pokud se liší od sídla firmy',
          helpText: 'Zadejte pouze pokud se liší od sídla zaměstnavatele',
          examples: [
            'Pobočka Praha - Karlín, Thámova 7',
            'Home office',
            'Dle potřeby - různé pobočky',
          ],
          ragEnabled: false,
        },
      ],
    },

    // ==================== STEP 3: Salary and Benefits ====================
    {
      title: 'Mzda a benefity',
      subtitle: 'Finanční podmínky zaměstnání',
      description: 'Odměňování a zaměstnanecké výhody',
      icon: 'Euro',
      color: 'error',
      fields: [
        {
          name: 'salary_amount',
          label: 'Výše mzdy',
          type: FieldType.NUMBER,
          validation: [
            { type: 'required', message: 'Výše mzdy je povinná' },
            { type: 'min', value: 1, message: 'Mzda musí být větší než 0' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '35000',
          helpText: 'Hrubá mzda v českých korunách',
          examples: ['35000', '50000', '80000'],
          min: 1,
          ragEnabled: false,
        },
        {
          name: 'salary_period',
          label: 'Období',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Období mzdy je povinné' },
          ],
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'Měsíční', label: 'Měsíční', icon: '📅' },
            { value: 'Hodinová', label: 'Hodinová', icon: '⏰' },
            { value: 'Týdenní', label: 'Týdenní', icon: '📆' },
          ],
        },
        {
          name: 'benefits',
          label: 'Benefity (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Popište zaměstnanecké benefity...',
          helpText: 'Například stravenky, benefit karty, firemní auto, apod.',
          examples: [
            'Stravenky 150 Kč/den, příspěvek na dopravu, flexibilní pracovní doba',
            'Multisport karta, 13. plat, příspěvek na penzijní připojištění',
            'Firemní auto, notebook, mobilní telefon, školení a konference',
            'Home office, sick days, team building akce, káva zdarma',
          ],
          ragEnabled: true,
        },
      ],
    },

    // ==================== STEP 4: Working Conditions ====================
    {
      title: 'Pracovní podmínky',
      subtitle: 'Doba a podmínky výkonu práce',
      description: 'Typ pracovního poměru, pracovní doba a další podmínky',
      icon: 'Schedule',
      color: 'primary',
      fields: [
        {
          name: 'employment_type',
          label: 'Typ pracovního poměru',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Typ pracovního poměru je povinný' },
          ],
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'Pracovní poměr na dobu neurčitou', label: 'Na dobu neurčitou', icon: '♾️' },
            { value: 'Pracovní poměr na dobu určitou', label: 'Na dobu určitou', icon: '⏰' },
            { value: 'Částečný úvazek', label: 'Částečný úvazek', icon: '📅' },
            { value: 'Dohoda o pracovní činnosti', label: 'DPČ', icon: '📝' },
            { value: 'Dohoda o provedení práce', label: 'DPP', icon: '📄' },
          ],
        },
        {
          name: 'start_date',
          label: 'Datum nástupu',
          type: FieldType.DATE,
          validation: [
            { type: 'required', message: 'Datum nástupu je povinné' },
          ],
          gridProps: { xs: 12, md: 6 },
          helpText: 'Kdy zaměstnanec nastupuje',
          ragEnabled: false,
        },
        {
          name: 'end_date',
          label: 'Datum ukončení',
          type: FieldType.DATE,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Kdy pracovní poměr končí',
          conditional: {
            field: 'employment_type',
            operator: 'equals',
            value: 'Pracovní poměr na dobu určitou',
            showWhenTrue: true,
          },
          ragEnabled: false,
        },
        {
          name: 'working_hours',
          label: 'Týdenní pracovní doba',
          type: FieldType.NUMBER,
          validation: [
            { type: 'required', message: 'Pracovní doba je povinná' },
            {
              type: 'pattern',
              value: /^([1-9]|[1-3][0-9]|40)$/,
              message: 'Pracovní doba musí být 1-40 hodin týdně',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '40',
          helpText: 'Počet hodin týdně',
          examples: ['40', '20', '30'],
          min: 1,
          max: 40,
          ragEnabled: false,
        },
        {
          name: 'trial_period',
          label: 'Zkušební doba (měsíce)',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: '3',
          helpText: 'Délka zkušební doby v měsících (max. 6)',
          examples: ['0', '3', '6'],
          min: 0,
          max: 6,
          ragEnabled: false,
        },
        {
          name: 'vacation_days',
          label: 'Dovolená (dny ročně)',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: '25',
          helpText: 'Počet dní dovolené ročně (min. 20)',
          examples: ['20', '25', '30'],
          min: 20,
          ragEnabled: false,
        },
        {
          name: 'flexible_hours',
          label: 'Flexibilní pracovní doba',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zaměstnanec má možnost flexibilní pracovní doby',
          defaultValue: false,
        },
        {
          name: 'home_office',
          label: 'Home office možný',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zaměstnanec může pracovat z domova',
          defaultValue: false,
        },
      ],
    },

    // ==================== STEP 5: Additional Terms ====================
    {
      title: 'Dodatečné podmínky',
      subtitle: 'Zvláštní ujednání a doložky',
      description: 'Konkurenční doložka, mlčenlivost a další podmínky',
      icon: 'Description',
      color: 'secondary',
      fields: [
        {
          name: 'confidentiality_clause',
          label: 'Doložka mlčenlivosti',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zaměstnanec je povinen zachovávat mlčenlivost o důvěrných informacích',
          defaultValue: false,
        },
        {
          name: 'non_compete_clause',
          label: 'Konkurenční doložka',
          type: FieldType.CHECKBOX,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Zaměstnanec nesmí po skončení pracovního poměru pracovat pro konkurenci',
          defaultValue: false,
        },
        {
          name: 'overtime_compensation',
          label: 'Kompenzace přesčasů',
          type: FieldType.SELECT,
          gridProps: { xs: 12 },
          options: [
            { value: '', label: 'Podle zákoníku práce', icon: '💰' },
            { value: 'Příplatek 25%', label: 'Příplatek 25%', icon: '💵' },
            { value: 'Náhradní volno', label: 'Náhradní volno', icon: '🏖️' },
            { value: 'Kombinace', label: 'Kombinace', icon: '🔄' },
          ],
          helpText: 'Způsob kompenzace práce přesčas',
        },
        {
          name: 'special_conditions',
          label: 'Zvláštní podmínky (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Zde můžete uvést jakékoli dodatečné podmínky...',
          helpText: 'Například školení, dress code, zvláštní povinnosti, apod.',
          examples: [
            'Zaměstnanec je povinen absolvovat školení GDPR do 30 dnů',
            'Povinnost nosit firemní oblečení při kontaktu s klienty',
            'Zákaz používání sociálních sítí v pracovní době',
            'Možnost čerpání neplacené dovolené až 30 dní ročně',
          ],
          ragEnabled: true,
        },
      ],
    },

    // Review step removed – preview is opened automatically in UI
  ],

  // ==================== RAG Configuration ====================
  rag: {
    enabled: true,
    fields: [
      'job_description',
      'benefits',
      'special_conditions',
    ],
    promptTemplate: `Analyzuj data pracovní smlouvy a poskytni inteligentní návrhy pro:
1. Kompletnost popisu pracovní náplně
2. Soulad s pracovněprávními předpisy
3. Vhodné benefity pro danou pozici
4. Doporučení pro dodatečné podmínky
5. Rizika a ochranná opatření`,
    minConfidence: 0.7,
    maxSuggestions: 5,
  },

  // ==================== Custom Validation ====================
  customValidation: (formData: any) => {
    const errors: Record<string, string> = {};

    // Validate minimum wage (as of 2025: 18,900 CZK/month for full-time)
    if (
      formData.employment_type === 'Pracovní poměr na dobu neurčitou' &&
      formData.salary_period === 'Měsíční' &&
      formData.working_hours === 40 &&
      formData.salary_amount < 18900
    ) {
      errors.salary_amount = 'Minimální mzda pro plný úvazek je 18 900 Kč měsíčně';
    }

    // Validate trial period maximum (3 months standard, 6 months for managerial positions)
    if (formData.trial_period && formData.trial_period > 6) {
      errors.trial_period = 'Zkušební doba nesmí přesáhnout 6 měsíců';
    }

    // Validate minimum vacation days (20 days by law)
    if (formData.vacation_days && formData.vacation_days < 20) {
      errors.vacation_days = 'Minimální zákonná dovolená je 20 dní ročně';
    }

    // Ensure end_date is after start_date for fixed-term contracts
    if (
      formData.employment_type === 'Pracovní poměr na dobu určitou' &&
      formData.start_date &&
      formData.end_date
    ) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);

      if (endDate <= startDate) {
        errors.end_date = 'Datum ukončení musí být po datu nástupu';
      }
    }

    // Validate working hours for part-time (must be less than 40)
    if (
      formData.employment_type === 'Částečný úvazek' &&
      formData.working_hours >= 40
    ) {
      errors.working_hours = 'Částečný úvazek musí mít méně než 40 hodin týdně';
    }

    // Validate DPČ hours (max 20 hours per week on average)
    if (
      formData.employment_type === 'Dohoda o pracovní činnosti' &&
      formData.working_hours > 20
    ) {
      errors.working_hours = 'DPČ může mít maximálně 20 hodin týdně v průměru';
    }

    // Validate DPP hours (max 300 hours per year)
    if (
      formData.employment_type === 'Dohoda o provedení práce' &&
      formData.working_hours > 10
    ) {
      errors.working_hours = 'DPP je omezena na maximálně 300 hodin ročně (cca 6 hodin týdně)';
    }

    // Warning for non-compete clause (must be reasonable and compensated)
    if (formData.non_compete_clause && !formData.special_conditions?.includes('kompenzace')) {
      // This is a warning, not blocking validation
      console.warn('Konkurenční doložka by měla obsahovat náhradu pro zaměstnance');
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  },

  // ==================== Data Transformation ====================
  transformData: (formData: any) => {
    // Transform data before submission
    const transformed = { ...formData };

    // Ensure salary_amount is a number
    if (transformed.salary_amount) {
      transformed.salary_amount = Number(transformed.salary_amount);
    }

    // Ensure numeric fields are numbers
    if (transformed.working_hours) {
      transformed.working_hours = Number(transformed.working_hours);
    }
    if (transformed.trial_period) {
      transformed.trial_period = Number(transformed.trial_period);
    }
    if (transformed.vacation_days) {
      transformed.vacation_days = Number(transformed.vacation_days);
    }

    // Format dates to ISO string
    if (transformed.start_date) {
      transformed.start_date = new Date(transformed.start_date).toISOString().split('T')[0];
    }
    if (transformed.end_date) {
      transformed.end_date = new Date(transformed.end_date).toISOString().split('T')[0];
    }

    // Ensure boolean fields are boolean
    transformed.flexible_hours = Boolean(transformed.flexible_hours);
    transformed.home_office = Boolean(transformed.home_office);
    transformed.confidentiality_clause = Boolean(transformed.confidentiality_clause);
    transformed.non_compete_clause = Boolean(transformed.non_compete_clause);

    // Add calculated fields
    if (transformed.salary_amount && transformed.salary_period === 'Měsíční') {
      transformed.annual_salary = transformed.salary_amount * 12;
    }

    return transformed;
  },
};

/**
 * Helper function to get employment type options
 *
 * @returns Array of select options for employment type field
 */
export const getEmploymentTypeOptions = () => {
  return [
    { value: 'Pracovní poměr na dobu neurčitou', label: 'Na dobu neurčitou', icon: '♾️' },
    { value: 'Pracovní poměr na dobu určitou', label: 'Na dobu určitou', icon: '⏰' },
    { value: 'Částečný úvazek', label: 'Částečný úvazek', icon: '📅' },
    { value: 'Dohoda o pracovní činnosti', label: 'DPČ', icon: '📝' },
    { value: 'Dohoda o provedení práce', label: 'DPP', icon: '📄' },
  ];
};

/**
 * Helper function to get step fields for validation
 *
 * Returns an array of required field names for a given step.
 * Used for step-by-step validation in multi-step forms.
 *
 * @param stepIndex - The step index (0-based)
 * @returns Array of field names for the given step
 *
 * @example
 * ```typescript
 * const fieldsToValidate = getEmploymentStepFields(0);
 * // Returns: ['employer_name', 'employer_address', 'employer_id']
 * ```
 */
export const getEmploymentStepFields = (stepIndex: number): string[] => {
  if (stepIndex < 0 || stepIndex >= employmentContractConfig.steps.length) {
    return [];
  }

  return employmentContractConfig.steps[stepIndex].fields
    .filter((field: FieldConfig) => field.validation?.some((rule: any) => rule.type === 'required'))
    .map((field: FieldConfig) => field.name);
};

/**
 * Helper function to get all field names from the configuration
 *
 * @returns Array of all field names in the employment contract form
 */
export const getAllEmploymentFields = (): string[] => {
  return employmentContractConfig.steps.flatMap((step) =>
    step.fields.map((field) => field.name)
  );
};

/**
 * Helper function to validate employment contract specific rules
 *
 * @param formData - The form data to validate
 * @returns Validation result with errors if any
 */
export const validateEmploymentContract = (formData: any) => {
  return employmentContractConfig.customValidation!(formData);
};

/**
 * Default values for testing/development
 * REMOVE IN PRODUCTION
 */
export const employmentContractTestData = {
  employer_name: 'TechSolutions s.r.o.',
  employer_address: 'Národní 25, 110 00 Praha 1',
  employer_id: '12345678',
  employer_contact: '+420 234 567 890',
  employee_name: 'Tomáš Procházka',
  employee_address: 'Karlovo náměstí 34, 120 00 Praha 2',
  employee_id: '890612/3456',
  employee_email: 'tomas.prochazka@email.cz',
  position_title: 'Frontend Developer',
  department: 'IT oddělení',
  job_description: 'Vývoj webových aplikací v React, TypeScript, spolupráce s týmem, code review',
  workplace_address: 'Národní 25, Praha 1 (možnost home office)',
  employment_type: 'Pracovní poměr na dobu neurčitou',
  start_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  end_date: '',
  trial_period: 3,
  salary_amount: 65000,
  salary_period: 'Měsíční',
  benefits: 'Stravné lístky, příspěvek na dopravu, školení, notebook',
  working_hours: 40,
  flexible_hours: true,
  home_office: true,
  overtime_compensation: 'Časové vyrovnání nebo příplatek 25%',
  vacation_days: 25,
  confidentiality_clause: true,
  non_compete_clause: false,
  special_conditions: 'Flexibilní pracovní doba 7:00-10:00 a 15:00-19:00, až 3 dny home office týdně',
};

export default employmentContractConfig;
